import { useState, useCallback, useEffect } from "react"
import { useParams } from "react-router"
import {
  stripLegacyToolTags,
  type ConversationPartApiResponse,
} from "@f2e/ui/chat-v2"
import { Conversation, ConversationGroup } from "@/types"
import { EvaluationCase, Evaluator } from "@/components/Evaluation"
import { UploadingFile } from "@/components/Conversation/FileUploadTypes"
import { FileBadgeData, FileAttachmentScope } from "@/types"
import {
  useGetEvaluatorsApi,
  evaluatorsResponseToEvaluators,
} from "@/hooks/api/evaluators"
import { filterToolTags } from "@/lib/conversationContent"

/**
 * Hook to manage evaluation modal state and transform conversation data
 * into evaluation case format for pre-filling the CaseDetailModal
 * Also manages fetching evaluators and context sources for the workspace
 */
export const useConversationEval = (workspaceIdParam?: string) => {
  const { workspaceId: workspaceIdFromParams } = useParams()
  const workspaceId = workspaceIdParam || workspaceIdFromParams

  const [isEvalModalOpen, setIsEvalModalOpen] = useState(false)
  const [evalCaseData, setEvalCaseData] = useState<EvaluationCase | null>(null)

  // Evaluators state
  const { data: evaluatorsResp, fetchFn: getEvaluators } = useGetEvaluatorsApi()
  const [evaluators, setEvaluators] = useState<Evaluator[]>([])

  // Fetch evaluators and context sources when workspace changes
  useEffect(() => {
    if (!workspaceId) return

    // Fetch evaluators
    getEvaluators({
      searchParams: new URLSearchParams({
        limit: "9999",
        wid: workspaceId,
      }),
    })
  }, [workspaceId, getEvaluators])

  // Process evaluators response
  useEffect(() => {
    if (!evaluatorsResp?.data) return
    const evaluatorsList = evaluatorsResponseToEvaluators(evaluatorsResp.data)
    setEvaluators(evaluatorsList)
  }, [evaluatorsResp])

  /**
   * Transform conversation attachments to UploadingFile format
   */
  const transformAttachments = useCallback(
    (attachedFiles?: FileBadgeData[]): UploadingFile[] => {
      if (!attachedFiles || attachedFiles.length === 0) return []

      return attachedFiles.map((file) => ({
        uuid: file.uuid,
        file: { name: file.name, size: 0, type: "" } as unknown as File, // dummy file
        status: "uploaded" as const,
        url: undefined,
        errorMessage: undefined,
        scope: FileAttachmentScope.Persistent,
      }))
    },
    []
  )

  /**
   * Get agent ID from conversation metadata
   */
  const extractAgentId = useCallback((question: Conversation): string => {
    return question.meta?.agentProfile?.id || ""
  }, [])

  /**
   * Transform conversation data into EvaluationCase format with separated history
   */
  const transformConversationToEvalCase = useCallback(
    (
      question: Conversation,
      answer: Conversation,
      allGroupedConversations: ConversationGroup[],
      sourceHistoryId?: number
    ): EvaluationCase => {
      const agentId = extractAgentId(question)
      const attachments = transformAttachments(question.attachedFiles)
      const previousConversationCount = allGroupedConversations.filter(
        (group) =>
          group.conversations.some(
            (conv) => conv.id && conv.id < (question.id ?? 0)
          )
      ).length
      const meta: EvaluationCase["meta"] = {}
      if (sourceHistoryId !== undefined) {
        meta.previousConversations = {
          sourceHistoryId,
          targetConversationId: question.id || 0,
          previousConversationCount,
        }
      }

      // Create a temporary evaluation case for the modal
      const evalCase: EvaluationCase = {
        id: "", // Temporary ID
        agentId,
        workspaceId: workspaceId || "",
        input: question.content,
        expectedOutput: filterToolTags(answer.content), // Pre-fill with AI response as reference
        attachments,
        meta,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      }

      return evalCase
    },
    [workspaceId, transformAttachments, extractAgentId]
  )

  const getPartContent = useCallback((part: ConversationPartApiResponse) => {
    const content = part.content.content
    if (typeof content === "string") return stripLegacyToolTags(content)
    if (content == null) return ""
    try {
      return JSON.stringify(content)
    } catch {
      return String(content)
    }
  }, [])

  const getPartAgentId = useCallback((part: ConversationPartApiResponse) => {
    const agentProfile = part.metadata.agent_profile
    if (
      typeof agentProfile === "object" &&
      agentProfile !== null &&
      "id" in agentProfile &&
      typeof agentProfile.id === "string"
    ) {
      return agentProfile.id
    }
    return ""
  }, [])

  const transformPartsToEvalCase = useCallback(
    (
      questionPart: ConversationPartApiResponse,
      answerPart: ConversationPartApiResponse,
      allParts: ConversationPartApiResponse[]
    ): EvaluationCase => {
      const targetId = questionPart.conversation_id ?? questionPart.id ?? 0
      const previousGroupIds = new Set<string>()
      for (const part of allParts) {
        if (part === questionPart) break
        if (part.part_kind === "user-prompt") {
          previousGroupIds.add(part.conversation_group_id)
        }
      }
      const meta: EvaluationCase["meta"] = {}
      if (questionPart.history_id !== undefined) {
        meta.previousConversations = {
          sourceHistoryId: questionPart.history_id,
          targetConversationId: targetId,
          previousConversationCount: previousGroupIds.size,
        }
      }

      return {
        id: "",
        agentId: getPartAgentId(questionPart) || getPartAgentId(answerPart),
        workspaceId: workspaceId || "",
        input: getPartContent(questionPart),
        expectedOutput: getPartContent(answerPart),
        attachments: [],
        meta,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      }
    },
    [getPartAgentId, getPartContent, workspaceId]
  )

  /**
   * Handle opening the eval modal with conversation data including history
   */
  const handleCreateEval = useCallback(
    (
      question: Conversation,
      answer: Conversation,
      allGroupedConversations: ConversationGroup[],
      sourceHistoryId?: number
    ) => {
      const evalCase = transformConversationToEvalCase(
        question,
        answer,
        allGroupedConversations,
        sourceHistoryId
      )
      setEvalCaseData(evalCase)
      setIsEvalModalOpen(true)
    },
    [transformConversationToEvalCase]
  )

  const handleCreateEvalFromParts = useCallback(
    (
      questionPart: ConversationPartApiResponse,
      answerPart: ConversationPartApiResponse,
      allParts: ConversationPartApiResponse[]
    ) => {
      const evalCase = transformPartsToEvalCase(
        questionPart,
        answerPart,
        allParts
      )
      setEvalCaseData(evalCase)
      setIsEvalModalOpen(true)
    },
    [transformPartsToEvalCase]
  )

  /**
   * Handle closing the eval modal
   */
  const handleCloseEvalModal = useCallback(() => {
    setIsEvalModalOpen(false)
    setEvalCaseData(null)
  }, [])

  return {
    isEvalModalOpen,
    evalCaseData,
    evaluators,
    handleCreateEval,
    handleCreateEvalFromParts,
    handleCloseEvalModal,
  }
}

export default useConversationEval
